import React, { useEffect, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  CardHeader,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import Select from 'react-select';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { slugify } from 'utils/slugify';
import {
  useCreateMutation,
  useUpdateMutation,
  useDetailQuery,
  useUpdatePatchMutation,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { handleFormErrors } from 'utils/formErrorHandler';
import FormField from 'components/Form/Inputs/FormField';
import ImageUploader from 'components/Form/Inputs/ImageUploader';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';

// Define the form values type based on ShopItem entity
interface CreateShopItemFormValues {
  sku: string;
  name: string;
  description?: string;
  imageUrl?: string;
  isActive: boolean;
  requiresPrescription?: boolean;
  severity?: string; // RiskCategory enum as string for form
}

// Strongly typed fields
const formFields: {
  id: keyof CreateShopItemFormValues;
  label: string;
  required?: boolean;
  disabled?: boolean;
  type?: string;
}[] = [
  { id: 'name', label: 'Item Name', required: true },
  { id: 'sku', label: 'SKU', required: true, disabled: true },
  // { id: 'imageUrl', label: 'Image URL' },
  { id: 'isActive', label: 'Is Active', type: 'checkbox' },
  { id: 'requiresPrescription', label: 'Requires Prescription', type: 'checkbox' },
  { id: 'severity', label: 'Severity', type: 'select' },
  { id: 'description', label: 'Description', type: 'textarea' },
];

// Severity options for react-select
const severityOptions = [
  { value: 'Normal', label: 'Normal' },
  { value: 'Mid', label: 'Mid' },
  { value: 'High', label: 'High' },
];

// Yup validation schema
const validationSchema = Yup.object({
  sku: Yup.string()
    .trim()
    .required('SKU is required')
    .max(255, 'SKU must be at most 255 characters'),
  name: Yup.string()
    .trim()
    .required('Item Name is required')
    .max(250, 'Item Name must be at most 250 characters'),
  description: Yup.string()
    .trim()
    .max(1000, 'Description must be at most 1000 characters')
    .nullable(),
  imageUrl: Yup.string()
    .trim()
    .url('Must be a valid URL')
    .max(255, 'Image URL must be at most 255 characters')
    .nullable(),
  isActive: Yup.boolean(),
  requiresPrescription: Yup.boolean().nullable(),
  severity: Yup.string().oneOf(['Normal', 'Mid', 'High'], 'Invalid severity level').nullable(),
});

//pas id as props
const AddUpdateMedicineItemForm: React.FC<{ id?: string }> = ({ id }) => {
  const { shop } = useShop();
  const { id: shopId } = shop!;
  const isEditMode = Boolean(id);

  const navigate = useNavigate();
  // Fetch existing shop item data for edit mode
  const { data: fetchedShopItem, isLoading: loadingShopItem } =
    useDetailQuery<CreateShopItemFormValues>(API_ENDPOINTS.TENANT.SHOP_ITEMS.WITH_ID, {
      id: id ?? '',
    });

  const { mutate: createShopItem, isPending: creating } = useCreateMutation(
    API_ENDPOINTS.TENANT.SHOP_ITEMS.BASE,
  );
  const { mutate: updateShopItem, isPending: updating } = useUpdatePatchMutation(
    API_ENDPOINTS.TENANT.SHOP_ITEMS.PATCH,
  );

  // Initial values - empty or from fetched data
  const initialValues: CreateShopItemFormValues = {
    sku: '',
    name: '',
    description: '',
    imageUrl: '',
    isActive: true,
    requiresPrescription: false,
    severity: 'Normal',
    ...fetchedShopItem,
  };

  const formik = useFormik<CreateShopItemFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<CreateShopItemFormValues>) => {
      const payload = {
        ...values,
        sku: values.sku.trim(),
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        imageUrl: values.imageUrl?.trim() || undefined,
        severity: values.severity || undefined,
      };

      const onSuccess = () => {
        navigate(SHOP_PATHS.ITEMS.replace(':shopId', shopId || ''));
      };

      const onError = (error: any) => {
        handleFormErrors<CreateShopItemFormValues>(error, formikHelpers);
      };

      if (isEditMode) {
        updateShopItem({ data: values, params: { id: id } }, { onSuccess, onError });
      } else {
        createShopItem(payload, { onSuccess, onError });
      }
    },
  });

  // Auto-generate SKU when name changes in create mode only
  useEffect(() => {
    if (!isEditMode && formik.values.name) {
      const generatedSKU = slugify(formik.values.name);
      formik.setFieldValue('sku', generatedSKU);
    }
  }, [formik.values.name, isEditMode]);

  // Clean SKU input on change to lowercase and allowed chars only
  const handleSKUChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    formik.setFieldValue('sku', cleaned);
  };

  // Handle select change for severity
  const handleSeverityChange = (option: { value: string; label: string } | null) => {
    formik.setFieldValue('severity', option ? option.value : undefined);
  };

  if (isEditMode && loadingShopItem) {
    return (
      <Container className="py-5 text-center">
        <Spinner color="primary" />
      </Container>
    );
  }
  return (
    <Row className="justify-content-center">
      <Col md={12} lg={8}>
        <Card className="p-4 shadow-sm">
          <CardHeader>
            <h2 className="text-center fw-bold fs-2">
              {isEditMode ? 'Update Medicine Item' : 'Add a New Medicine Item'}
            </h2>
          </CardHeader>

          <CardBody>
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="d-flex justify-content-center">
                <ImageUploader
                  value={formik.values.imageUrl}
                  onChange={(url) => formik.setFieldValue('imageUrl', 'url')}
                />
              </div>

              <Row className="mt-0">
                {formFields.map((field) => (
                  <Col key={field.id} sm={12} md={6} lg={6}>
                    <FormField
                      id={field.id}
                      label={field.label}
                      type={field.type}
                      required={field.required}
                      disabled={field.disabled}
                      value={formik.values[field.id]}
                      touched={formik.touched[field.id]}
                      error={formik.errors[field.id]}
                      onChange={field.id === 'sku' ? handleSKUChange : formik.handleChange}
                      onBlur={formik.handleBlur}
                      options={field.type === 'select' ? severityOptions : undefined}
                      onSelectChange={field.type === 'select' ? handleSeverityChange : undefined}
                    />
                  </Col>
                ))}
              </Row>

              <div className="d-flex justify-content-center mt-4">
                <Button color="primary" type="submit" disabled={creating || updating}>
                  {creating || updating
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                    ? 'Update Medicine Item'
                    : 'Add Medicine Item'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default memo(AddUpdateMedicineItemForm);
